import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from "chart.js";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import useAdminAuth from "./admin/hooks/useAdminAuth";
import useAdminAnalytics from "./admin/hooks/useAdminAnalytics";
import useAdminTriggers from "./admin/hooks/useAdminTriggers";
import useAdminLocales from "./admin/hooks/useAdminLocales";
import useAdminRoulette from "./admin/hooks/useAdminRoulette";
import useAdminPhotosession from "./admin/hooks/useAdminPhotosession";
import useAdminPhotoAnimation from "./admin/hooks/useAdminPhotoAnimation";
import useAdminTemplates from "./admin/hooks/useAdminTemplates";
import useAdminUsers from "./admin/hooks/useAdminUsers";
import useAdminMailing from "./admin/hooks/useAdminMailing";
import useAdminLogs from "./admin/hooks/useAdminLogs";
import useAdminAiOffers from "./admin/hooks/useAdminAiOffers";
import useAdminSubscriptions from "./admin/hooks/useAdminSubscriptions";
import AdminLoginGate from "./admin/sections/AdminLoginGate";
import AdminAppShell from "./admin/layout/AdminAppShell";
import AnalyticsPage from "./admin/pages/AnalyticsPage";
import TriggersPage from "./admin/pages/TriggersPage";
import LocalesPage from "./admin/pages/LocalesPage";
import RoulettePageAdmin from "./admin/pages/RoulettePageAdmin";
import PhotosessionPage from "./admin/pages/PhotosessionPage";
import PhotoAnimationPage from "./admin/pages/PhotoAnimationPage";
import TemplatesPage from "./admin/pages/TemplatesPage";
import StatsPage from "./admin/pages/StatsPage";
import UsersPage from "./admin/pages/UsersPage";
import CampaignsPage from "./admin/pages/CampaignsPage";
import LogsPage from "./admin/pages/LogsPage";
import AiOffersPage from "./admin/pages/AiOffersPage";
import TriggerRecordsPage from "./admin/pages/TriggerRecordsPage";
import PromoCodesPage from "./admin/pages/PromoCodesPage";
import SubscriptionsPage from "./admin/pages/SubscriptionsPage";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function matchesAdminPath(pathname, routePath) {
  return pathname === routePath || pathname.startsWith(`${routePath}/`);
}

export default function AdminPage() {
  const location = useLocation();
  const auth = useAdminAuth();

  const analyticsEnabled =
    auth.verified &&
    (matchesAdminPath(location.pathname, "/admin/analytics") ||
      matchesAdminPath(location.pathname, "/admin/stats") ||
      matchesAdminPath(location.pathname, "/admin/campaigns"));
  const analyticsAutoRefresh = !matchesAdminPath(location.pathname, "/admin/analytics/requests");
  const triggersEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/triggers");
  const localesEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/locales");
  const rouletteEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/roulette");
  const photosessionEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/photosession");
  const photoAnimationEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/photo-animation");
  const templatesEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/templates");
  const logsEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/logs");
  const campaignsEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/campaigns");
  const offersEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/offers");
  const promoCodesEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/promo-codes");
  const subscriptionsEnabled = auth.verified && matchesAdminPath(location.pathname, "/admin/subscriptions");

  const analytics = useAdminAnalytics({
    adminToken: auth.adminToken,
    enabled: analyticsEnabled,
    autoRefresh: analyticsAutoRefresh
  });
  const triggers = useAdminTriggers({
    adminToken: auth.adminToken,
    enabled: triggersEnabled
  });
  const locales = useAdminLocales({
    adminToken: auth.adminToken,
    enabled: localesEnabled
  });
  const roulette = useAdminRoulette({
    adminToken: auth.adminToken,
    enabled: rouletteEnabled
  });
  const photosession = useAdminPhotosession({
    adminToken: auth.adminToken,
    enabled: photosessionEnabled
  });
  const photoAnimation = useAdminPhotoAnimation({
    adminToken: auth.adminToken,
    enabled: photoAnimationEnabled
  });
  const templates = useAdminTemplates({
    adminToken: auth.adminToken,
    enabled: templatesEnabled
  });
  const users = useAdminUsers({
    adminToken: auth.verified ? auth.adminToken : ""
  });
  const logs = useAdminLogs({
    adminToken: auth.adminToken,
    enabled: logsEnabled
  });
  const offers = useAdminAiOffers({
    adminToken: auth.adminToken,
    enabled: offersEnabled
  });
  const mailing = useAdminMailing({
    adminToken: auth.adminToken,
    enabled: campaignsEnabled,
    stats: analytics.data.stats
  });
  const subscriptions = useAdminSubscriptions({
    adminToken: auth.adminToken,
    enabled: subscriptionsEnabled
  });

  if (!auth.verified) {
    return (
      <AdminLoginGate
        code={auth.code}
        loading={auth.loading}
        onCodeChange={auth.setCode}
        onVerify={auth.verify}
      />
    );
  }

  return (
    <Routes>
      <Route element={<AdminAppShell onLogout={auth.logout} />}>
        <Route index element={<Navigate to="analytics" replace />} />
        <Route path="analytics" element={<AnalyticsPage analytics={analytics} adminToken={auth.adminToken} />} />
        <Route
          path="analytics/requests"
          element={<AnalyticsPage analytics={analytics} adminToken={auth.adminToken} />}
        />
        <Route path="triggers" element={<TriggersPage triggers={triggers} />} />
        <Route path="triggers/:id" element={<TriggersPage triggers={triggers} />} />
        <Route path="trigger-records" element={<TriggerRecordsPage adminToken={auth.adminToken} />} />
        <Route path="locales" element={<LocalesPage auth={auth} locales={locales} />} />
        <Route path="roulette" element={<RoulettePageAdmin roulette={roulette} />} />
        <Route path="photosession" element={<PhotosessionPage photosession={photosession} />} />
        <Route path="photo-animation" element={<PhotoAnimationPage photoAnimation={photoAnimation} />} />
        <Route path="templates" element={<TemplatesPage templates={templates} />} />
        <Route path="stats" element={<StatsPage stats={analytics.data.stats} />} />
        <Route path="users" element={<UsersPage users={users} />} />
        <Route path="campaigns" element={<CampaignsPage mailing={mailing} />} />
        <Route path="logs" element={<LogsPage logs={logs} />} />
        <Route path="offers" element={<AiOffersPage offers={offers} />} />
        <Route path="promo-codes" element={<PromoCodesPage adminToken={auth.adminToken} enabled={promoCodesEnabled} />} />
        <Route path="subscriptions" element={<SubscriptionsPage subscriptions={subscriptions} />} />
        <Route path="*" element={<Navigate to="analytics" replace />} />
      </Route>
    </Routes>
  );
}
